import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { useSelectionBarVisibility } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarVisibility';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { Pathname } from '~/constants/pathname';
import { Routes } from '~/constants/routes';
import { hasOnlyCanvasesSelectedSelector } from '~/store/selectedItems/selectors';

export type UseStrandedCanvasSelectionCleanupParams = {
  /**
   * Layout entry survives navigation — watch pathname instead of the per-page unmount cleanup.
   * `PrivateWorkspaceAgentSpace` passes true; `PrivateBoardsAndAssetsSelectionBar` uses the default.
   */
  watchPathname?: boolean;
};

/**
 * Clears a canvas-only selection when it has followed the user somewhere canvases are not
 * first-class content.
 *
 * Per-page bars also clear on unmount so selections do not bleed across component instances
 * (e.g. canvases → board). The layout entry outlives those navigations and watches pathname.
 */
export const useStrandedCanvasSelectionCleanup = ({
  watchPathname = false,
}: UseStrandedCanvasSelectionCleanupParams = {}) => {
  const dispatch = useDispatch();
  const { pathname } = useRouter();
  const { hasStrandedCanvasSelection } = useSelectionBarVisibility();
  const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);

  const hasOnlyCanvasesSelectedRef = useRef(hasOnlyCanvasesSelected);
  hasOnlyCanvasesSelectedRef.current = hasOnlyCanvasesSelected;

  const previousPathnameRef = useRef<string | null>(null);

  const clearStrandedSelection = useCallback(() => {
    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [dispatch, resetSelectAll]);

  useEffect(() => {
    if (watchPathname || !hasStrandedCanvasSelection) {
      return;
    }

    clearStrandedSelection();
  }, [watchPathname, hasStrandedCanvasSelection, clearStrandedSelection]);

  useEffect(() => {
    if (!watchPathname) {
      return;
    }

    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;

    if (previousPathname === null || previousPathname === pathname) {
      return;
    }

    if (!hasOnlyCanvasesSelectedRef.current) {
      return;
    }

    const isBoardOrCanvasesPage =
      pathname === Pathname.BoardByBoardId || pathname === Pathname.BoardCanvas || pathname === Routes.canvases;

    if (isBoardOrCanvasesPage) {
      return;
    }

    clearStrandedSelection();
  }, [watchPathname, clearStrandedSelection, pathname]);

  useEffect(() => {
    if (watchPathname) {
      return;
    }

    return () => {
      if (hasOnlyCanvasesSelectedRef.current) {
        clearStrandedSelection();
      }
    };
  }, [watchPathname, clearStrandedSelection]);
};
